// Ringtone (/settings/ringtone, native only): shows the current incoming-call
// ringtone and lets the user change it via the system picker. Bridged to the
// native call-service plugin behind the shell's composition rule — probe
// isCapacitorNative() first, then dynamically import shell/capacitor so no
// @capacitor/core reaches the web bundle. The `pickRingtone` /
// `getRingtoneTitle` plugin methods are provided by the native shell.

import { useEffect, useState, type ReactNode } from 'react';
import { isCapacitorNative } from '../../shell/capacitor/platform.js';
import { useT } from '../../i18n/index.js';
import { Section, buttonClass } from './chrome.js';

/** The subset of the native call-service plugin this screen drives. */
interface RingtoneCallService {
  getRingtoneTitle(): Promise<{ title: string | null }>;
  pickRingtone(): Promise<{ title: string | null }>;
}

/** Test seam: resolves the current ringtone title and opens the system picker. */
export interface RingtoneBridge {
  getTitle(): Promise<{ title: string | null }>;
  pick(): Promise<{ title: string | null }>;
}

function createRingtoneBridge(): RingtoneBridge {
  // Cache the MODULE, never the plugin proxy: resolving a promise WITH a
  // Capacitor plugin proxy makes the JS thenable-check access `.then` on it,
  // which the proxy forwards as a native call — "MerkuryCallService.then() is
  // not implemented" — and the promise never settles (field-debugged on
  // device). The proxy must only ever be method-called, not awaited.
  let mod: Promise<typeof import('../../shell/capacitor/index.js')> | undefined;
  const getModule = (): NonNullable<typeof mod> =>
    (mod ??= import('../../shell/capacitor/index.js'));
  return {
    async getTitle() {
      const native = await getModule();
      return (native.nativePlugins().callService as RingtoneCallService).getRingtoneTitle();
    },
    async pick() {
      const native = await getModule();
      return (native.nativePlugins().callService as RingtoneCallService).pickRingtone();
    },
  };
}

/**
 * App-wide ringtone control, embedded in the Calls settings group. Native-only
 * (the system picker is an Android plugin): renders nothing on the web, where
 * the browser has no ringtone concept. Guards before the hooks-bearing body so
 * the native bridge is never touched on web.
 */
export function RingtoneSection({
  ringtone,
}: {
  /** Test seam; defaults to the real Capacitor call-service plugin. */
  ringtone?: RingtoneBridge;
} = {}): ReactNode {
  if (!ringtone && !isCapacitorNative()) return null;
  return <RingtoneSettings ringtone={ringtone} />;
}

function RingtoneSettings({ ringtone }: { ringtone: RingtoneBridge | undefined }): ReactNode {
  const t = useT();
  const [bridge] = useState<RingtoneBridge>(() => ringtone ?? createRingtoneBridge());
  const [title, setTitle] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void bridge
      .getTitle()
      .then(({ title }) => {
        if (cancelled) return;
        setTitle(title);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [bridge]);

  async function handleChange(): Promise<void> {
    setBusy(true);
    try {
      const result = await bridge.pick();
      setTitle(result.title);
      setLoaded(true);
    } catch {
      // System picker dismissed / unavailable: keep the current title.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title={t('ringtone.title')}>
      <p className="text-xs text-muted">{t('ringtone.help')}</p>
      <p className="text-sm text-ink">
        {t('ringtone.current')}{' '}
        <span data-testid="ringtone-title" className="text-ink">
          {loaded ? (title ?? t('ringtone.default')) : '…'}
        </span>
      </p>
      <button type="button" disabled={busy} onClick={() => void handleChange()} className={buttonClass}>
        {t('ringtone.change')}
      </button>
    </Section>
  );
}
